import { useEffect, useMemo, useState } from "react"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ShoppingCart, FileText, ArrowRight, Loader as Loader2 } from "lucide-react"
import { useShelvingStore } from "@/lib/shelving-store"
import { useLanguageStore } from "@/lib/language-store"
import { useAuthStore } from "@/lib/auth-store"
import { usePricingStore, resolvePricingContext } from "@/lib/pricing-store"
import { useDomainStore } from "@/lib/domain-store"
import { Slider } from "@/components/ui/slider"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Checkbox } from "@/components/ui/checkbox"
import { BOMDialog } from "@/components/bom-dialog"
import { OrderQuotationDialog } from "@/components/order-quotation-dialog"
import { SaveToCartDialog } from "@/components/save-to-cart-dialog"
import { fetchMaterials, MaterialData } from "@/lib/materials-service"
import { calculateBOM, BOMSummary } from "@/lib/bom-calculator"
import { saveConfiguration, SaveConfigurationResult } from "@/lib/save-configuration-service"

const toMM = (cm: number) => `${cm * 10}`

export function ConfigurationControls() {
  const { getTranslation } = useLanguageStore()
  const { user, profile, reseller } = useAuthStore()
  const { simulatedReseller } = usePricingStore()
  const { domainReseller, domainChecked } = useDomainStore()
  const isLoggedIn = !!user
  const isAdmin = profile?.role === 'admin'
  const isReseller = profile?.role === 'reseller'
  const isPublicDomain = domainChecked && !!domainReseller && !isLoggedIn
  const [bom, setBOM] = useState<BOMSummary | null>(null)
  const [materials, setMaterials] = useState<MaterialData[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<SaveConfigurationResult | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const {
    model,
    height,
    width,
    depth,
    sides,
    shelfCount,
    sectionCount,
    frameState,
    showBOMDialog,
    showOrderDialog,
    setModel,
    setHeight,
    setWidth,
    setDepth,
    setSides,
    setShelfCount,
    setSectionCount,
    setFrameState,
    setShowBOMDialog,
    setShowOrderDialog,
    setShelfLoadPerShelf,
  } = useShelvingStore()

  useEffect(() => {
    fetchMaterials().then(setMaterials)
  }, [])

  const role = profile?.role ?? null
  const discountPercent = simulatedReseller?.discount_percent ?? reseller?.discount_percent ?? null
  const marginPercent = simulatedReseller?.margin_percent ?? reseller?.margin_percent ?? null

  const pricingContext = useMemo(
    () => resolvePricingContext(role, reseller, simulatedReseller, domainReseller),
    [role, discountPercent, marginPercent, simulatedReseller?.id, domainReseller?.id]
  )

  useEffect(() => {
    if (materials.length === 0) return
    const config = { height, width, depth, shelfCount, sectionCount, sides, frameState, model }
    const newBOM = calculateBOM(materials, config, pricingContext ?? undefined)
    setBOM(newBOM)
    const shelfItem = newBOM.items.find(item => item.category === 'Shelf SF')
    setShelfLoadPerShelf(shelfItem?.load ?? 0)
  }, [materials, height, width, depth, shelfCount, sectionCount, sides, frameState, model, pricingContext])

  const shelfBOMItem = bom?.items.find(item => item.category === 'Shelf SF')
  const singleShelfLoad = shelfBOMItem?.load || 0

  const shelfWidthMM = width * 10
  const shelfDepthMM = depth * 10
  const shelfType = model === 'L2505' ? 'Type L 2505' : 'Type M 4006'
  const currentShelfMaterial = materials.find(m =>
    m.category === 'Shelf SF' &&
    m.length === shelfWidthMM &&
    m.width === shelfDepthMM &&
    m.description.includes(shelfType)
  )
  const maxShelvesLimit = currentShelfMaterial?.maxShelves ?? 0
  const isShelfLimitExceeded = maxShelvesLimit > 0 && shelfCount > maxShelvesLimit

  const resellerShopUrl = isReseller ? (reseller?.shop_url ?? null) : null
  const resellerCartUrl = isReseller ? (reseller?.cart_url ?? null) : null
  const resellerHasCart = !!(resellerShopUrl || resellerCartUrl)
  const domainShopUrl = (isPublicDomain && domainReseller?.shop_url) ? domainReseller.shop_url : null
  const domainCartUrl = (isPublicDomain && domainReseller?.cart_url && domainReseller?.cart_api_key_id) ? domainReseller.cart_url : null
  const domainHasCart = !!(domainShopUrl || domainCartUrl)
  const showCartButton = isAdmin || (isReseller && resellerHasCart) || domainHasCart

  const handleAddToCart = async () => {
    if (!bom) return
    setIsSaving(true)
    setSaveResult(null)
    setSaveError(null)

    const label = `${model} ${width * 10}×${height * 10}×${depth * 10}mm — ${shelfCount} shelves, ${sectionCount} section${sectionCount !== 1 ? 's' : ''}, ${sides}`

    const isPublicDomainSave = !isReseller && !isAdmin && domainHasCart
    const { data, error } = await saveConfiguration({
      label,
      price: bom.totalPrice,
      config: { model, height, width, depth, shelfCount, sectionCount, sides, frameState },
      bom,
      useEdgeFunction: isPublicDomainSave,
    })

    setIsSaving(false)

    if (error || !data) {
      setSaveError(error ?? 'Unknown error')
      setShowSaveDialog(true)
      return
    }

    const activeShopUrl = resellerShopUrl ?? domainShopUrl
    if (activeShopUrl) {
      const separator = activeShopUrl.includes('?') ? '&' : '?'
      const target = `${activeShopUrl}${separator}add_config=1&configurator=shelving&config_id=${encodeURIComponent(data.config_id)}`
      window.location.href = target
      return
    }

    const activeLegacyCartUrl = resellerCartUrl ?? domainCartUrl
    if (activeLegacyCartUrl) {
      const cartUrl = activeLegacyCartUrl.replace('{config_id}', encodeURIComponent(data.config_id))
      window.location.href = cartUrl
      return
    }

    setSaveResult(data)
    setShowSaveDialog(true)
  }

  return (
    <Card className="border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
      <CardContent className="pt-3">
        {isReseller && reseller && (
          <p
            className="text-sm font-semibold text-center mb-1 tracking-wide"
            style={{ color: reseller.brand_color ?? '#212b4f' }}
          >
            {reseller.name}
          </p>
        )}
        {isAdmin && simulatedReseller && (
          <p
            className="text-sm font-semibold text-center mb-1 tracking-wide"
            style={{ color: simulatedReseller.brand_color ?? '#212b4f' }}
          >
            {simulatedReseller.name}{' '}
            <span className="font-normal text-xs opacity-70">(simulation)</span>
          </p>
        )}
        {isPublicDomain && (
          <p
            className="text-sm font-semibold text-center mb-1 tracking-wide"
            style={{ color: domainReseller!.brand_color ?? '#212b4f' }}
          >
            {domainReseller!.name}
          </p>
        )}
        <h2 className="text-xl font-bold text-[#212b4f] text-center mb-4">
          3D Shelving Configurator
        </h2>
        <Tabs defaultValue="configuration" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="configuration">{getTranslation("tab.configuration")}</TabsTrigger>
            <TabsTrigger value="options">{getTranslation("tab.options")}</TabsTrigger>
          </TabsList>

          <TabsContent value="configuration" className="space-y-3">
            {/* Shelf Type */}
            <div className="space-y-1.5">
              <Label className="text-foreground text-sm font-medium">{getTranslation("shelf.type")}</Label>
              <div className="flex gap-2">
                <Button
                  variant={model === "L2505" ? "brand-solid" : "brand-outline"}
                  onClick={() => setModel("L2505")}
                  className="h-8 flex-1 text-sm border border-[#212b4f] px-2 py-1 rounded-2xl"
                >
                  L 2505
                </Button>
                <Button
                  variant={model === "M4006" ? "brand-solid" : "brand-outline"}
                  onClick={() => setModel("M4006")}
                  className="h-8 flex-1 text-sm border border-[#212b4f] px-2 py-1 rounded-2xl"
                >
                  M 4006
                </Button>
              </div>
            </div>

            {/* Width */}
            <div className="space-y-1.5">
              <Label className="text-foreground text-sm font-medium">{getTranslation("shelf.length")}</Label>
              <div className="flex gap-2">
                {[97, 127].map((val) => (
                  <Button
                    key={val}
                    variant={width === val ? "brand-solid" : "brand-outline"}
                    onClick={() => setWidth(val)}
                    className="h-8 flex-1 text-sm border border-[#212b4f] px-2 py-1 rounded-2xl"
                  >
                    {toMM(val)} mm
                  </Button>
                ))}
              </div>
            </div>

            {/* Height */}
            <div className="space-y-1.5">
              <Label className="text-foreground text-sm font-medium">{getTranslation("frame.height")}</Label>
              <div className="flex gap-2">
                {[200, 220, 250].map((val) => (
                  <Button
                    key={val}
                    variant={height === val ? "brand-solid" : "brand-outline"}
                    onClick={() => setHeight(val)}
                    className="h-8 flex-1 text-sm border border-[#212b4f] px-2 py-1 rounded-2xl"
                  >
                    {toMM(val)} mm
                  </Button>
                ))}
              </div>
            </div>

            {/* Depth */}
            <div className="space-y-1.5">
              <Label className="text-foreground text-sm font-medium">{getTranslation("shelf.depth")}</Label>
              <div className="grid grid-cols-3 gap-2">
                {[40, 50, 60].map((val) => (
                  <Button
                    key={val}
                    variant={depth === val ? "brand-solid" : "brand-outline"}
                    onClick={() => setDepth(val)}
                    className="h-8 text-sm border border-[#212b4f] px-2 py-1 rounded-2xl"
                  >
                    {toMM(val)} mm
                  </Button>
                ))}
              </div>
            </div>

            {/* Sides */}
            <div className="space-y-1.5">
              <Label className="text-foreground text-sm font-medium">{getTranslation("frame.type")}</Label>
              <div className="flex gap-2">
                {["single", "double"].map((val) => (
                  <Button
                    key={val}
                    variant={sides === val ? "brand-solid" : "brand-outline"}
                    onClick={() => setSides(val as "single" | "double")}
                    className="h-8 flex-1 text-sm border border-[#212b4f] px-2 py-1 rounded-2xl"
                  >
                    {getTranslation(`${val}.side`)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Frame State */}
            <div className="space-y-1.5">
              <Label className="text-foreground text-sm font-medium">{getTranslation("frame")}</Label>
              <div className="flex gap-2 flex-wrap">
                {["open", "closed", "outside"].map((val) => (
                  <Button
                    key={val}
                    variant={frameState === val ? "brand-solid" : "brand-outline"}
                    onClick={() => setFrameState(val as any)}
                    className="h-8 flex-1 text-sm border border-[#212b4f] px-2 py-1 rounded-2xl"
                  >
                    {getTranslation(val)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Shelves and Sections Sliders */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Shelves Slider */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-3">
                  <Label className="text-foreground text-sm font-medium">{getTranslation("shelves")}</Label>
                  <span className={`text-xl font-bold ${isShelfLimitExceeded ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'}`}>{shelfCount}</span>
                </div>
                <Slider
                  value={[shelfCount]}
                  onValueChange={(value) => setShelfCount(value[0])}
                  min={3}
                  max={maxShelvesLimit > 0 ? maxShelvesLimit : 15}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>3</span>
                  <span>{maxShelvesLimit > 0 ? maxShelvesLimit : 15}</span>
                </div>
                {isShelfLimitExceeded && (
                  <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                    {getTranslation("exceeds.max.shelves").replace("{max}", String(maxShelvesLimit))}
                  </p>
                )}
              </div>

              {/* Sections Slider */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-3">
                  <Label className="text-foreground text-sm font-medium">{getTranslation("sections")}</Label>
                  <span className="text-xl font-bold text-foreground">{sectionCount}</span>
                </div>
                <Slider
                  value={[sectionCount]}
                  onValueChange={(value) => setSectionCount(value[0])}
                  min={1}
                  max={25}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>1</span>
                  <span>25</span>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="options" className="space-y-3">
            <div className="space-y-3 py-2">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="dustcaps"
                  checked={true}
                  disabled={true}
                />
                <Label
                  htmlFor="dustcaps"
                  className="text-foreground text-sm font-medium cursor-not-allowed opacity-50"
                >
                  {getTranslation("dustcaps")}
                </Label>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Price and Add to Cart - Below Tabs */}
        <div className="mt-4 space-y-3">
          {singleShelfLoad > 0 && (
            <div className="text-center py-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {getTranslation("shelf.load")}: <span className="font-semibold">{singleShelfLoad} kg</span>
              </p>
            </div>
          )}

          <div className="pt-1 flex items-center justify-center">
            {(isLoggedIn || isPublicDomain) && bom ? (
              <div className="font-bold text-3xl text-green-600 dark:text-green-400">
                €{Math.round(bom.totalPrice)}
              </div>
            ) : (
              <div className="text-sm font-medium text-slate-500 italic">
                Price on request
              </div>
            )}
          </div>

          <div className="space-y-2">
            {(isReseller || isPublicDomain) && (
              <Button
                onClick={() => setShowOrderDialog(true)}
                className="w-full gap-2 bg-[#2e78c7] hover:bg-[#2568b0] text-white h-9 text-sm px-3 py-1.5 rounded-2xl"
              >
                <ArrowRight className="h-4 w-4" />
                {getTranslation("proceed.with.order")}
              </Button>
            )}

            {isAdmin && (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => setShowBOMDialog(true)}
                  variant="outline"
                  className="gap-2 h-9 text-sm px-3 py-1.5 border-[#212b4f] text-[#212b4f] hover:bg-[#212b4f] hover:text-white rounded-2xl"
                >
                  <FileText className="h-4 w-4" />
                  {getTranslation("bom.button")}
                </Button>
                <Button
                  onClick={handleAddToCart}
                  disabled={isSaving || !bom}
                  className="gap-2 bg-green-600 hover:bg-green-700 active:bg-green-800 border border-green-600 hover:border-green-700 text-white transition-colors duration-200 h-9 text-sm px-3 py-1.5 rounded-2xl disabled:opacity-60"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShoppingCart className="h-4 w-4" />
                  )}
                  {getTranslation("add.to.cart")}
                </Button>
              </div>
            )}

            {isReseller && resellerHasCart && (
              <Button
                onClick={handleAddToCart}
                disabled={isSaving || !bom}
                className="w-full gap-2 bg-green-600 hover:bg-green-700 active:bg-green-800 border border-green-600 hover:border-green-700 text-white transition-colors duration-200 h-10 text-sm rounded-2xl disabled:opacity-60"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShoppingCart className="h-4 w-4" />
                )}
                {getTranslation("add.to.cart")}
              </Button>
            )}

            {isPublicDomain && domainHasCart && (
              <Button
                onClick={handleAddToCart}
                disabled={isSaving || !bom}
                className="w-full gap-2 bg-green-600 hover:bg-green-700 active:bg-green-800 border border-green-600 hover:border-green-700 text-white transition-colors duration-200 h-10 text-sm rounded-2xl disabled:opacity-60"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShoppingCart className="h-4 w-4" />
                )}
                {getTranslation("add.to.cart")}
              </Button>
            )}
          </div>

        </div>

        <BOMDialog open={showBOMDialog} onOpenChange={setShowBOMDialog} />
        <OrderQuotationDialog open={showOrderDialog} onOpenChange={(open) => setShowOrderDialog(open)} />
        <SaveToCartDialog
          open={showSaveDialog}
          onOpenChange={setShowSaveDialog}
          configId={saveResult?.config_id ?? null}
          label={saveResult?.label ?? null}
          price={saveResult?.price ?? null}
          error={saveError}
        />
      </CardContent>
    </Card>
  )
}
